import { TextField } from "@mui/material";
import { ChangeEvent, FC, useCallback, WheelEvent, KeyboardEvent } from "react";
import { TextFieldProps } from "@mui/material/TextField/TextField";

type NumberFieldProps = Omit<TextFieldProps, "type" | "onWheel"> & {
  maxValue?: number;
  minValue?: number;
};

export const NumberField: FC<NumberFieldProps> = ({
  maxValue,
  minValue,
  onChange,
  InputProps,
  ...props
}) => {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);
      if (
        ((maxValue && value > maxValue) || (minValue && value < minValue)) &&
        event.target.value !== ""
      ) {
        return;
      }

      onChange?.(event);
    },
    [onChange, minValue, maxValue],
  );

  const disableOnWheelEvent = useCallback((e: WheelEvent<HTMLDivElement>) => {
    const target = e.target as HTMLInputElement;
    target.blur();
  }, []);

  const preventNonNumericInput = useCallback((e: KeyboardEvent) => {
    const key = e.key;
    const allowedKeys = [
      "Backspace",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      ".",
      "-",
    ];

    const isAllowedKey = allowedKeys.includes(key);
    const isDigit = /^\d$/.test(key);

    if (!isDigit && !isAllowedKey) {
      e.preventDefault();
    }
  }, []);

  return (
    <TextField
      {...props}
      type="number"
      onChange={handleChange}
      onWheel={disableOnWheelEvent}
      onKeyDown={(e) => (isFirefox ? preventNonNumericInput(e) : undefined)}
      InputProps={{
        inputProps: {
          min: minValue,
          max: maxValue,
          ...(InputProps?.inputProps ?? []),
        },
        ...InputProps,
      }}
    />
  );
};

const isFirefox =
  typeof window !== "undefined" &&
  "InstallTrigger" in window &&
  /firefox/i.test(navigator?.userAgent);
